<template>
  <v-dialog
    v-if="props.isOpened"
    v-model="props.isOpened"
    :transition="transition"
    persistent
    scrollable
    v-bind="dialogProps"
  >
    <v-card>
      <header class="header">
        <v-card-title>{{ props.title }}</v-card-title>
      </header>
      <v-card-text class="sub-header" v-if="props.subTitle">
        <span v-html="props.subTitle"></span>
      </v-card-text>
      <v-card-text class="card-text">
        <slot name="body">
          <slot name="prepend"></slot>
          <LfRequiredFieldsLegendComponent :showRequiredFieldsAdvice="props.showRequiredFieldsAdvice" />
          <FormGeneratorComponent
            :schema="props.formSchema"
            :fieldValues="props.formFieldValues"
            :context="props.context"
            :parentField="props.parentField"
            formTag="selectForm"
            class="form-generator"
            @execute-action="executeAction"
            @saveFormData="onSaveFormData"
          />
        </slot>
      </v-card-text>
      <v-card-actions v-if="props.showActionButtons">
        <v-spacer></v-spacer>
        <LfActionsBarComponent
          class="action-buttons"
          :buttons="buttons"
          @execute-action="executeAction"
          :showKebabMenu="false"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Icons } from '@/icons/icons'
import { ActionName } from '@/components/ActionsBar/types/ActionBarComponentTypes'
import { ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import LfActionsBarComponent from '@/components/ActionsBar/LfActionsBarComponent.vue'
import { LabelButtonVariant } from '@/components/forms/buttons/types/ButtonTypes'
import LfRequiredFieldsLegendComponent from '@/components/forms/fields/LfRequiredFiedsLegend/LfRequiredFiedsLegendComponent.vue'
import FormGeneratorComponent from '@/components/forms/FormGenerator/FormGeneratorComponent.vue'
import store from '@/store/store'

const props = defineProps({
  width: {
    type: Number,
    default: 352
  },
  title: {
    type: String,
    required: true
  },
  hideOverlay: {
    type: Boolean,
    default: false
  },
  subTitle: {
    type: String,
    default: ''
  },
  context: {
    type: String,
    required: true
  },
  isOpened: {
    type: Boolean,
    default: false
  },
  showActionButtons: {
    type: Boolean,
    default: true
  },
  transition: {
    type: String,
    default: ''
  },
  formSchema: {
    type: Array,
    required: true,
    default: () => []
  },
  formFieldValues: {
    type: Object,
    default: () => ({})
  },
  texts: {
    type: Object,
    default: () => ({
      closeButton: 'Cancelar',
      saveButton: 'Guardar'
    })
  },
  showRequiredFieldsAdvice: {
    type: Boolean,
    default: true
  },
  parentField: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['execute-action', 'changeFormField'])

const executeAction = (value: string) => {
  emit('execute-action', value)
}

const buttons = computed(() => [
  {
    icon: Icons.CLOSE,
    tooltip: props.texts.closeButton,
    class: 'secondary-color',
    action: ActionName.CLOSE,
    variant: LabelButtonVariant.OUTLINED,
    enabled: true
  },
  {
    icon: Icons.CHECK,
    tooltip: props.texts.saveButton,
    class: 'main-color',
    action: ActionName.SAVE,
    enabled: store.getters[`${ModuleNamespaces.FORMS}/checkIfFormIsValid`](props.context)
  }
])

const dialogProps = computed(() => {
  return {
    width: props.width,
    title: props.title,
    'hide-overlay': props.hideOverlay
  }
})

const onSaveFormData = (schema: any[], formData: object, field: any, value: any) => {
  emit('changeFormField', schema, formData, field, value)
}
</script>

  <style lang="scss" scoped>
  .v-dialog__content {
    background-color: $dialog-background-color;
    pointer-events: unset;

    .required-fields-advice {
      @include milano-regular-14;
      display: flex;
      position: relative;
      color: $neutral-grey-600;
      align-self: flex-end;
      padding-right: 16px;
      width: 100%;
      justify-content: flex-end;

      &:before {
        @include milano-bold-20;
        display: inline-block;
        position: relative;
        top: -4px;
        content: '*';
        color: $subject-fireEngineRed;
        margin-right: 3px;
      }
    }

    ::v-deep .v-dialog {
      border-radius: $cornerRadius-md;
      border: 1px solid $main-200;
      background: $neutral-white-000;
      box-shadow: $shadow-md;

      .form-generator .form {
        width: 100%;
      }

      .v-card {
        background-color: $white-01;
        border-radius: 0;
        padding: $spacing-xl;

        .header {
          display: flex;
          align-items: center;
          height: 46px;
          padding: $spacing-xs;
          border-radius: $cornerRadius-sm;
          background: $main-1000;

          .v-card__title {
            @include ellipsis;
            @include milano-bold-18;
            color: $neutral-white-000;
            padding: 0px $spacing-xs;
          }
        }

        .sub-header {
          @include flex;
          span {
            @include flex;
            font-family: $corporate-font;
            color: $blue-04;
            font-size: 20px;
            line-height: 24px;
            text-align: center;
            padding: 20px 0;
          }
        }

        .v-card__actions {
          padding: 0;
        }

        .form-generator > div {
          padding: 0 !important;
        }

        .card-text {
          @include scroll-styles;
          overflow-x: hidden;
          padding: 0;
          margin-top: $spacing-xl;
          > div {
            @include flex($flex-direction: column);

            > div {
              padding: 0;
            }
          }
        }

        .action-buttons {
          margin-top: $spacing-xl;
        }
      }
    }
  }
  </style>
